import { useLocation } from "react-router-dom"
import { ExpenseState } from "../../../../utils/ExpenseState"
import { useToggle } from "../../../../utils/Hooks"
import { SinglePathSVG } from "../../SVG"
import React from "react"
import { useIntl } from "react-intl"
import Settings from "../../../../utils/CarrotConfig"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  OpenFileIcon,
  useMediaQuery,
  useTheme,
  CircleArrowUpIcon
} from "@carrotfertility/carotene-core"
import { isMobileApp } from "#/lib/carrot-api/MobileNativeClient"
import { UploadAdditionalDocumentsFlow } from "#/components/upload-additional-documents"
import { GetReimbursed } from "#/components/get-reimbursed"

export function getFileOnClick(guid: string) {
  return () => {
    if (isMobileApp()) {
      window.location.href = `${Settings.CARROT_BACKEND_URL}/api/v2/statements/files/${guid}`
    } else {
      window.open(`${Settings.CARROT_BACKEND_URL}/api/v2/statements/files/${guid}`, "_blank", "noreferrer")
    }
  }
}

const FileDrawingLink = ({ guid }: { guid: string }) => {
  const intl = useIntl()

  return (
    <IconButton
      aria-label={intl.formatMessage({ defaultMessage: "View document" })}
      onClick={getFileOnClick(guid)}
      sx={(theme) => ({
        marginBlockStart: theme.spacing(theme.tokens.spacing.xs)
      })}
    >
      <OpenFileIcon />
    </IconButton>
  )
}
const FilePendingScan = () => {
  const [showPendingScanModal, togglePendingScanModal] = useToggle()
  const intl = useIntl()
  const theme = useTheme()
  const PendingScanModal = () => (
    <EmployeeOnlyModalContainer onClose={togglePendingScanModal}>
      <Dialog
        open={showPendingScanModal}
        onClose={togglePendingScanModal}
        aria-labelledby="pending-scan-modal-title"
        aria-describedby="pending-scan-modal-description"
      >
        <Box display="flex" justifyContent="flex-end">
          <IconButton
            aria-label={intl.formatMessage({ defaultMessage: "close" })}
            onClick={() => togglePendingScanModal()}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogTitle id="pending-scan-modal-title">
          {intl.formatMessage({ defaultMessage: "This file is currently unavailable" })}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.lg, marginBlockEnd: theme.tokens.spacing.xxxl })}
          >
            <DialogContentText id="pending-scan-modal-description">
              {intl.formatMessage({
                defaultMessage: "We're scanning your file for corruption. Please try again later."
              })}
            </DialogContentText>
          </Box>
        </DialogContent>
      </Dialog>
    </EmployeeOnlyModalContainer>
  )
  return (
    <>
      <IconButton onClick={togglePendingScanModal}>
        <SinglePathSVG icon="statementFile" fill={theme.palette.primary.main} />
      </IconButton>
      {showPendingScanModal && <PendingScanModal />}
    </>
  )
}
// @ts-expect-error TS(7031) FIXME: Binding element 'reimbursement' implicitly has an ... Remove this comment to see the full error message
export const Attachments = ({ reimbursement }) => {
  const intl = useIntl()
  const queryParams = new URLSearchParams(useLocation().search)
  const enableUpload = reimbursement.expenseState === ExpenseState.PENDING_MEMBER_INFO

  const expenseId = queryParams.get("upload-expense-id")
  const isModalInitiallyOpen = expenseId && parseInt(expenseId, 10) === reimbursement.expenseId
  const isPreAuth = !!reimbursement?.preAuthId
  const [isReimbursementsModalOpen, toggleIsReimbursementsModalOpen] = useToggle(isModalInitiallyOpen && !isPreAuth)
  const [isPreAuthModalOpen, toggleIsPreAuthModalOpen] = useToggle(isModalInitiallyOpen && isPreAuth)
  const theme = useTheme()
  const showInFullscreen = useMediaQuery(theme.breakpoints.up("sm"))

  if (enableUpload) {
    return (
      <>
        <IconButton
          onClick={() => (isPreAuth ? toggleIsPreAuthModalOpen() : toggleIsReimbursementsModalOpen())}
          aria-label={intl.formatMessage({ defaultMessage: "Upload documents" })}
        >
          <CircleArrowUpIcon color="primary" />
        </IconButton>
        {isPreAuthModalOpen && (
          <UploadAdditionalDocumentsFlow
            expenseId={reimbursement.expenseId}
            onExit={toggleIsPreAuthModalOpen}
            isOpen={isPreAuthModalOpen}
          />
        )}
        {isReimbursementsModalOpen && (
          <GetReimbursed
            expenseId={reimbursement.expenseId}
            transDate={reimbursement.submittedDatetime}
            transAmount={reimbursement.amount}
            isOpen={isReimbursementsModalOpen}
            onExit={toggleIsReimbursementsModalOpen}
          />
        )}
      </>
    )
  } else {
    return (
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row-reverse",
          flexWrap: "wrap",
          justifyContent: "start",
          paddingBlockStart: showInFullscreen ? "" : theme.spacing(theme.tokens.spacing.xs)
        })}
      >
        {reimbursement.fileGuids.map((guid: string) => (
          <FileDrawingLink key={guid} guid={guid} />
        ))}
        {[...Array(reimbursement.filesPendingScanCount)].map((x, i) => (
          <FilePendingScan key={`pendingFile-${i}`} />
        ))}
      </Box>
    )
  }
}
